import React from 'react'
import clsx from 'clsx'
import Image from 'components/Image/SimpleImage'
import PropTypes from 'prop-types'
import Markdown from 'react-markdown'
import { motion } from 'framer-motion'
import { ensureGetData } from 'lib/helpers'
import { Container, Columns, Column } from 'bloomer'
import { useInView } from 'react-intersection-observer'
import { trackFor } from '../../../lib/tracker/utils'

// Motion Variants
import * as motionVariants from './motion.variants'

// Style
import styles from './style.module.scss'
import ensureBasePath from '../../../lib/ensureBasePath'

export const HeroHeader = ({ data }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.3,
  })

  return (
    <div className={clsx(styles.hero_header, styles.hero_header__inverse)} ref={ref}>
      <div className={styles.image_wrapper}>
        <motion.div
          variants={motionVariants.imageContainer}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          exit="hidden">
          <motion.img
            variants={motionVariants.image}
            className={styles.hero_image_halo}
            src={ensureBasePath('/images/about/header-halo.png?resize&size=512')}
            alt=""
          />
          <motion.img
            variants={motionVariants.image}
            className={styles.hero_image}
            src={ensureBasePath('/images/about/header-copy.png?inline')}
            alt=""
          />
          <motion.img
            variants={motionVariants.image}
            className={styles.hero_image}
            src={ensureBasePath('/images/about/header-people.png')}
            alt=""
          />
        </motion.div>

        <motion.div
          className={styles.decoration_wrapper}
          variants={motionVariants.bgImageContainer}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          exit="hidden">
          <div className={styles.decoration_mobile_wrapper}>
            <motion.div
              variants={motionVariants.decorationMobile}
              className={clsx(styles.bg_decoration, styles.bg_decoration_mobile_bottom)}>
              <div className={clsx(styles.block_wrapper, styles.block_wrapper_1)}>
                <div className={styles.block_1_mobile} />
              </div>
            </motion.div>

            <motion.div
              variants={motionVariants.decorationMobile}
              className={clsx(styles.bg_decoration, styles.bg_decoration_mobile_top)}>
              <div className={clsx(styles.block_wrapper, styles.block_wrapper_2)}>
                <div className={styles.block_2_mobile} />
              </div>
            </motion.div>
          </div>

          <div className={styles.bg_decoration}>
            <motion.div
              className={clsx(styles.block_wrapper, styles.block_wrapper_1)}
              variants={motionVariants.decorationDesktop}>
              <div className={styles.block_1} />
            </motion.div>
          </div>

          <div className={styles.bg_decoration}>
            <motion.div
              className={clsx(styles.block_wrapper, styles.block_wrapper_2)}
              variants={motionVariants.decorationDesktop}>
              <div className={styles.block_2} />
            </motion.div>
          </div>
        </motion.div>
      </div>

      <Container className={styles.hero_container}>
        <Columns>
          <Column isSize={{ tablet: 6 }}>
            <motion.div
              variants={motionVariants.container}
              initial="hidden"
              animate={inView ? 'visible' : 'hidden'}
              exit="hidden">
              <motion.div variants={motionVariants.child}>
                <h1 className={styles.hero_title}>{ensureGetData(data, 'title')}</h1>
              </motion.div>

              <motion.div variants={motionVariants.child}>
                <p className={styles.hero_subtitle}>{ensureGetData(data, 'tagline', '')}</p>
              </motion.div>

              <motion.div variants={motionVariants.child}>
                <Markdown className={clsx('section-copy', styles.hero_content)}>
                  {ensureGetData(data, 'description')}
                </Markdown>
              </motion.div>

              <motion.div variants={motionVariants.child}>
                <div className={styles.cta_wrapper}>
                  <a
                    href={ensureGetData(data, 'url', '#')}
                    className={styles.btn_cta}
                    onClick={() => trackFor({ gtm: { event: 'explore_career' } })}>
                    <div className={styles.bg} />
                    <span style={{ marginRight: '1em' }}>Explore Career</span>
                    <Image
                      width={20}
                      height={13}
                      className={styles.arrow}
                      src={ensureBasePath('/images/icons/hero-arrow.svg')}
                      alt=">"
                    />
                  </a>
                </div>
              </motion.div>
            </motion.div>
          </Column>
        </Columns>
      </Container>
    </div>
  )
}

HeroHeader.defaultProps = {
  data: {},
}

HeroHeader.propTypes = {
  data: PropTypes.object.isRequired,
}

export default HeroHeader
